import { useEffect, useRef, useCallback } from 'react';
import { createLogger } from '../utils/logger';

const log = createLogger('Polling');

/**
 * Hook for polling data at regular intervals
 */
export function usePolling<T>(
  fetchFn: () => Promise<T>,
  onSuccess: (data: T) => void,
  intervalMs: number = 3000,
  enabled: boolean = true
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  const poll = useCallback(async () => {
    if (!mountedRef.current || !enabled) return;

    try {
      log.debug(`Polling cycle (interval=${intervalMs}ms)`);
      const data = await fetchFn();
      if (mountedRef.current) {
        onSuccess(data);
      }
    } catch (error) {
      log.warn('Polling error (will retry):', error);
    }

    if (mountedRef.current && enabled) {
      timeoutRef.current = setTimeout(poll, intervalMs);
    }
  }, [fetchFn, onSuccess, intervalMs, enabled]);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      poll();
    }

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [poll, enabled]);

  const refresh = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    poll();
  }, [poll]);

  return { refresh };
}
